/**
 * UpdateBody — renders the expanded detail for an `update` version.
 *
 * Backend audit (Commit 5 audit step):
 *   diff = { kind: 'update', agent_name, array_key, id_field,
 *            record_id, field_changes: FieldChange[] }
 *
 *   - Standard agents: verified at record_operations.py:266-288
 *   - Item Definition (analysis_agent): verified at
 *     record_operations.py:1436-1458. `array_key`/`id_field` are
 *     null and `record_id` is the literal string `item_definition`.
 *     Field paths are dotted (deep) because the diff is computed
 *     by `_compute_deep_field_diff`.
 *
 *   Fields the mockup had that the backend does NOT ship (omitted
 *   from this body):
 *     - section_name   (mock-only — was used for breadcrumb)
 *     - record_snapshot (mock-only — was used for "Current state"
 *       MiniRow preview above the field diffs)
 *
 * Render: caption row + a stack of FieldDiff rows. Collection is
 * omitted for analysis_agent because it has no array_key.
 */
import React from 'react';
import { VersionRecord, UpdateDiff } from '../../state/types';
import FieldDiff from '../FieldDiff/FieldDiff';
import styles from './operationBodies.module.css';

interface UpdateBodyProps {
  version: VersionRecord;
}

export default function UpdateBody({ version }: UpdateBodyProps) {
  const diff = version.diff as UpdateDiff | null;
  if (!diff || diff.kind !== 'update' || !version.agent_name) return null;

  const changes = diff.field_changes ?? [];
  const isItemDefinition = version.agent_name === 'analysis_agent';

  return (
    <div className={styles.body}>
      <div className={styles.detailHeader}>
        <span>
          Edited <code>{diff.record_id}</code>
        </span>
        {!isItemDefinition && diff.array_key && (
          <span>
            Collection <code>{diff.array_key}</code>
          </span>
        )}
        <span>
          {changes.length} field{changes.length === 1 ? '' : 's'} changed
        </span>
      </div>
      <div>
        {changes.map((c, i) => (
          <FieldDiff
            key={`${c.field}-${i}`}
            agentName={version.agent_name as string}
            change={c}
          />
        ))}
      </div>
    </div>
  );
}
